import React from 'react';
import { Gift, CreditCard, Plane, ShoppingBag, Wifi, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const RewardsPreview = () => {
  const { t, isRTL } = useLanguage();

  const rewards = [
    { 
      icon: ShoppingBag, 
      category: t.rewardsPreview.examples[0].category,
      card: t.rewardsPreview.examples[0].card,
      reward: t.rewardsPreview.examples[0].reward,
      color: '#FF9500'
    },
    { 
      icon: Plane, 
      category: t.rewardsPreview.examples[1].category,
      card: t.rewardsPreview.examples[1].card,
      reward: t.rewardsPreview.examples[1].reward,
      color: '#0071E3'
    },
    { 
      icon: ShoppingBag, 
      category: t.rewardsPreview.examples[2].category,
      card: t.rewardsPreview.examples[2].card,
      reward: t.rewardsPreview.examples[2].reward,
      color: '#34C759'
    },
    { 
      icon: Wifi, 
      category: t.rewardsPreview.examples[3].category,
      card: t.rewardsPreview.examples[3].card,
      reward: t.rewardsPreview.examples[3].reward,
      color: '#5856D6'
    }
  ];

  return (
    <section className="py-20 md:py-24 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className={`${isRTL ? 'lg:order-2 text-right' : ''}`}>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF9500]/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Gift className="w-3.5 h-3.5 text-[#FF9500]" />
              <span className="text-[11px] font-semibold text-[#FF9500] uppercase tracking-wide font-inter">
                {t.rewardsPreview.badge}
              </span>
            </div>
            <h2 className="font-dm text-3xl md:text-4xl font-semibold tracking-tight text-[#1D1D1F] mb-4 leading-tight">
              {t.rewardsPreview.title}
            </h2>
            <p className="text-lg text-[#6e6e73] font-inter mb-8">
              {t.rewardsPreview.subtitle}
            </p>

            {/* Reward Cards */}
            <div className="space-y-3">
              {rewards.map((reward, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-xl p-4 border border-[#E5E5E5]/60 hover:shadow-md transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                >
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${reward.color}15` }}
                      >
                        <reward.icon className="w-5 h-5" style={{ color: reward.color }} />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <p className="font-inter font-medium text-[#1D1D1F] text-[15px]">{reward.category}</p>
                        <p className="text-[#86868b] text-[13px] font-inter">{reward.card}</p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="font-dm font-semibold text-[#1D1D1F]" style={{ color: reward.color }}>
                        {reward.reward}
                      </span>
                      <ArrowRight className={`w-4 h-4 text-[#86868b] ${isRTL ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Visual */}
          <div className={`${isRTL ? 'lg:order-1' : ''}`}>
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF9500]/20 to-[#FFCC00]/10 blur-3xl rounded-3xl scale-105"></div>
              
              {/* Card */}
              <div className="relative bg-gradient-to-br from-[#1D1D1F] to-[#2D2D2F] rounded-3xl p-8 overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#FF9500]/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#5856D6]/20 rounded-full blur-3xl"></div>
                
                <div className={`relative z-10 ${isRTL ? 'text-right' : ''}`}>
                  <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#FF9500] to-[#FFCC00] rounded-xl flex items-center justify-center">
                      <Gift className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-[12px] font-inter">This Month</p>
                      <p className="text-white font-dm font-semibold text-lg">Rewards Earned</p>
                    </div>
                  </div>
                  
                  <p className="text-5xl font-dm font-bold text-white mb-2">AED 342</p>
                  <p className="text-white/50 text-[14px] font-inter mb-6">+18% vs last month</p>
                  
                  <div className="bg-white/10 rounded-xl p-4">
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Sparkles className="w-5 h-5 text-[#FF9500]" />
                      <div className={isRTL ? 'text-right' : ''}>
                        <p className="text-white/80 text-[13px] font-inter">Potential savings identified</p>
                        <p className="text-white font-dm font-semibold">AED 184 more possible</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RewardsPreview;
